import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Input,
    Text,
    Textarea,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Flex,
    VStack,
    HStack,
    Heading,
    Grid,
    GridItem,
    Switch,
    Icon,
    IconButton,
    Spinner,
    useToast,
    Tag,
    Center
} from '@chakra-ui/react';
import {
    LuPlus,
    LuSearch,
    LuShield,
    LuAlertTriangle,
    LuAlertOctagon,
    LuTrash2,
    LuBan,
    LuCheck,
    LuFileEdit,
    LuShieldCheck,
} from 'react-icons/lu';
import Header from '../../../components/Header';
import API from '../../../helpers/axiosInstance';
import usePagePermissions from '../../../hooks/usePagePermissions';
import BoxWrapper from '../../../components/BoxWrapper/BoxWrapper';
import PageHeading from '../../../components/shared/PageHeading';

export default function SafeMessageFilter() {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [isAddKeywordOpen, setIsAddKeywordOpen] = useState(false);
    const [isEditKeywordOpen, setIsEditKeywordOpen] = useState(false);
    const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
    const [isEditCategoryOpen, setIsEditCategoryOpen] = useState(false);
    const [keywordSearchTerm, setKeywordSearchTerm] = useState('');
    const [isDeleteKeywordOpen, setIsDeleteKeywordOpen] = useState(false);
    const [keywordToDelete, setKeywordToDelete] = useState(null);

    const [categories, setCategories] = useState([]);
    const [keywords, setKeywords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [keywordLoading, setKeywordLoading] = useState(false);

    // Button loading states
    const [createCategoryLoading, setCreateCategoryLoading] = useState(false);
    const [updateCategoryLoading, setUpdateCategoryLoading] = useState(false);
    const [createKeywordLoading, setCreateKeywordLoading] = useState(false);
    const [updateKeywordLoading, setUpdateKeywordLoading] = useState(false);
    const [deleteKeywordLoading, setDeleteKeywordLoading] = useState(false);

    // Form states
    const [newKeyword, setNewKeyword] = useState('');
    const [newKeywordNotes, setNewKeywordNotes] = useState('');
    const [editKeywordData, setEditKeywordData] = useState(null);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [editCategoryData, setEditCategoryData] = useState(null);

    const toast = useToast();
    const { loading: permissionsLoading, can } = usePagePermissions('tools/safe-message-filter');

    const listPermission = can("safe-msg-cat-list")
    const createPermission = can("safe-msg-cat-create")
    const editPermission = can("safe-msg-cat-edit")
    const keywordlistPermission = can("safe-msg-list")
    const keywordcreatePermission = can("safe-msg-create")
    const keywordeditPermission = can("safe-msg-edit")
    const statusPermission = can("safe-msg-cat-status")
    const keywordStatusPermission = can("safe-msg-status")
    const keyworddeletePermission = can("safe-msg-delete")
    const [categoryContextDenied, setCategoryContextDenied] = useState(false);
    const canViewCategories = (listPermission || keywordlistPermission) && !categoryContextDenied;
    const getApiHeaders = () => ({
        "x-api-key": localStorage.getItem("api-key"),
        "x-access-token": localStorage.getItem("customer-portal"),
    });

    // Fetch Categories
    const fetchCategories = async () => {
        setLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/safe-category/list`;
            const response = await API.get(url, { headers: getApiHeaders() });

            if (response.data.success) {
                const mappedCategories = response.data.data.map(cat => ({
                    id: cat.id,
                    name: cat.name,
                    enabled: cat.status_id === 1,
                    keywordCount: cat.keyword_count || 0,
                    icon: 'Shield',
                    createdAt: cat.created_at,
                    updatedAt: cat.updated_at,
                    keyword_matches: cat.keyword_matches,
                    customer_id: cat.customer_id
                }));
                setCategories(mappedCategories);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
            if (!listPermission) {
                // Keyword-list-only user: categories were only fetched as read-only
                // context for picking keywords — fall back to the explanation panel.
                setCategoryContextDenied(true);
                return;
            }
            toast({
                title: "Error",
                description: "Failed to fetch categories",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    // Fetch Keywords by Category
    const fetchKeywords = async (categoryId) => {
        setKeywordLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/safe-msg/list?category_id=${categoryId}`;
            const response = await API.get(url, { headers: getApiHeaders() });

            if (response.data.success) {
                const mappedKeywords = response.data.data.map(kw => ({
                    id: kw.id,
                    keyword: kw.keyword_name,
                    notes: kw.notes,
                    status: kw.status_id === 1 ? 'active' : 'inactive',
                    categoryId: kw.category_id,
                    lastUpdated: new Date(kw.updated_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                    }),
                    customer_id: kw.customer_id
                }));
                setKeywords(mappedKeywords);
            }
        } catch (error) {
            console.error("Error fetching keywords:", error);
            toast({
                title: "Error",
                description: "Failed to fetch keywords",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setKeywordLoading(false);
        }
    };

    // Create Category
    const handleCreateCategory = async () => {
        if (!newCategoryName.trim()) {
            toast({
                title: "Validation Error",
                description: "Category name is required",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setCreateCategoryLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/safe-category/create`;
            const payload = { name: newCategoryName.trim() };
            const response = await API.post(url, payload, { headers: getApiHeaders() });

            if (response.data.success) {
                toast({
                    title: "Success",
                    description: "Category created successfully",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                setNewCategoryName('');
                setIsAddCategoryOpen(false);
                fetchCategories();
            }
        } catch (error) {
            console.error("Error creating category:", error);
            toast({
                title: "Error",
                description: "Failed to create category",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setCreateCategoryLoading(false);
        }
    };

    // Update Category
    const handleUpdateCategory = async () => {
        if (!editCategoryData?.name.trim()) {
            toast({
                title: "Validation Error",
                description: "Category name is required",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setUpdateCategoryLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/safe-category/update`;
            const payload = {
                id: editCategoryData.id,
                name: editCategoryData.name.trim()
            };
            const response = await API.post(url, payload, { headers: getApiHeaders() });

            if (response.data.success) {
                toast({
                    title: "Success",
                    description: "Category updated successfully",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                setIsEditCategoryOpen(false);
                setEditCategoryData(null);
                fetchCategories();
            }
        } catch (error) {
            console.error("Error updating category:", error);
            toast({
                title: "Error",
                description: "Failed to update category",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setUpdateCategoryLoading(false);
        }
    };

    // Create Keyword
    const handleCreateKeyword = async () => {
        if (!newKeyword.trim() || !selectedCategory) {
            toast({
                title: "Validation Error",
                description: "Keyword is required",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setCreateKeywordLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/safe-msg/create`;
            const payload = {
                category_id: selectedCategory.id,
                keyword: newKeyword.trim(),
                notes: newKeywordNotes.trim() || ''
            };
            const response = await API.post(url, payload, { headers: getApiHeaders() });

            if (response.data.success) {
                toast({
                    title: "Success",
                    description: "Keyword created successfully",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                setNewKeyword('');
                setNewKeywordNotes('');
                setIsAddKeywordOpen(false);
                fetchKeywords(selectedCategory.id);
                fetchCategories(); // Refresh keyword count
            }
        } catch (error) {
            console.error("Error creating keyword:", error);
            toast({
                title: "Error",
                description: "Failed to create keyword",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setCreateKeywordLoading(false);
        }
    };

    // Update Keyword
    const handleUpdateKeyword = async () => {
        if (!editKeywordData?.keyword.trim()) {
            toast({
                title: "Validation Error",
                description: "Keyword is required",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setUpdateKeywordLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/safe-msg/update`;
            const payload = {
                id: editKeywordData.id,
                category_id: editKeywordData.categoryId,
                keyword: editKeywordData.keyword.trim(),
                notes: editKeywordData.notes.trim() || ''
            };
            const response = await API.post(url, payload, { headers: getApiHeaders() });

            if (response.data.success) {
                toast({
                    title: "Success",
                    description: "Keyword updated successfully",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                setIsEditKeywordOpen(false);
                setEditKeywordData(null);
                fetchKeywords(selectedCategory.id);
            }
        } catch (error) {
            console.error("Error updating keyword:", error);
            toast({
                title: "Error",
                description: "Failed to update keyword",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setUpdateKeywordLoading(false);
        }
    };

    // Delete Keyword
    const handleDeleteKeyword = async () => {
        if (!keywordToDelete) return;

        setDeleteKeywordLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/safe-msg/delete`;
            const payload = { id: keywordToDelete.id };
            const response = await API.post(url, payload, { headers: getApiHeaders() });

            if (response.data.success) {
                toast({
                    title: "Success",
                    description: "Keyword deleted successfully",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                setIsDeleteKeywordOpen(false);
                setKeywordToDelete(null);
                fetchKeywords(selectedCategory.id);
                fetchCategories(); // Refresh keyword count
            }
        } catch (error) {
            console.error("Error deleting keyword:", error);
            toast({
                title: "Error",
                description: "Failed to delete keyword",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setDeleteKeywordLoading(false);
        }
    };

    // Toggle Category Status
    const handleToggleCategoryStatus = async (category) => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/safe-category/update-status`;
            const payload = {
                id: category.id,
                status_id: category.enabled ? 0 : 1
            };
            const response = await API.post(url, payload, { headers: getApiHeaders() });

            if (response.data.success) {
                toast({
                    title: "Success",
                    description: `Category ${category.enabled ? 'deactivated' : 'activated'} successfully`,
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                fetchCategories();
            }
        } catch (error) {
            console.error("Error toggling category status:", error);
            toast({
                title: "Error",
                description: "Failed to update category status",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    // Toggle Keyword Status
    const handleToggleKeywordStatus = async (keyword) => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/safe-msg/update-status`;
            const payload = {
                id: keyword.id,
                status_id: keyword.status === 'active' ? 0 : 1
            };
            const response = await API.post(url, payload, { headers: getApiHeaders() });

            if (response.data.success) {
                toast({
                    title: "Success",
                    description: `Keyword ${keyword.status === 'active' ? 'deactivated' : 'activated'} successfully`,
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                fetchKeywords(selectedCategory.id);
            }
        } catch (error) {
            console.error("Error toggling keyword status:", error);
            toast({
                title: "Error",
                description: "Failed to update keyword status",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    // Open Edit Modals
    const openEditCategoryModal = (category) => {
        setEditCategoryData({ id: category.id, name: category.name });
        setIsEditCategoryOpen(true);
    };

    const openEditKeywordModal = (keyword) => {
        setEditKeywordData(keyword);
        setIsEditKeywordOpen(true);
    };

    // Open Delete Confirmation Modal
    const openDeleteKeywordModal = (keyword) => {
        setKeywordToDelete(keyword);
        setIsDeleteKeywordOpen(true);
    };

    // Handle category selection
    const handleCategorySelect = (category) => {
        setSelectedCategory(category);
        fetchKeywords(category.id);
        setKeywordSearchTerm('');
    };

    // RP_007: keywords only exist inside a category, so keyword-list users also
    // need the category rail to pick one — load it for them too (read-only:
    // edit/status/create controls stay behind their own category slugs).
    useEffect(() => {
        if (permissionsLoading || !canViewCategories) return;
        fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [permissionsLoading, canViewCategories]);

    const getIconComponent = (iconName) => {
        const icons = {
            Shield: LuShield,
            AlertTriangle: LuAlertTriangle,
            AlertOctagon: LuAlertOctagon,
            Ban: LuBan
        };
        return icons[iconName] || LuShield;
    };

    const filteredCategories = categories.filter(cat =>
        cat.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredKeywords = keywords.filter(keyword =>
        keyword.keyword.toLowerCase().includes(keywordSearchTerm.toLowerCase())
    );

    if (permissionsLoading) {
        return (
            <>
                <Header />
                <BoxWrapper>
                    <Center h="400px">
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                    </Center>
                </BoxWrapper>
            </>
        );
    }

    if (!listPermission && !keywordlistPermission) {
        return (
            <>
                <Header />
                <BoxWrapper>
                    <Center h="400px">
                        <Box textAlign="center">
                            <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
                                No Access
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                                You don't have permission to view this sections.
                            </Text>
                        </Box>
                    </Center>
                </BoxWrapper>
            </>
        );
    }

    if (loading) {
        return (
            <>
                <Header />
                <BoxWrapper>
                    <Center h="400px">
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                    </Center>
                </BoxWrapper>
            </>
        );
    }

    return (
        <>
            <Header />
            <BoxWrapper>
                <Box >
                    <VStack spacing={6} align="stretch">
                        {/* Top Header */}
                        <PageHeading icon={LuShieldCheck} title="Safe Message Filter" subtitle="Manage keyword filtering rules based on compliance regulations" mb={0}>
                            {createPermission && (

                                <Button
                                    bg="var(--brand-primary)"
                                    color="white"
                                    _hover={{ bg: 'var(--brand-primary-hover)' }}
                                    leftIcon={<Icon as={LuPlus} />}
                                    onClick={() => setIsAddCategoryOpen(true)}
                                    size={"sm"}
                                >
                                    Add Category
                                </Button>
                            )}
                        </PageHeading>

                        {/* Search Bar */}
                        <Box maxW="md" position="relative">
                            <Icon as={LuSearch} position="absolute" left="12px" top="50%" transform="translateY(-50%)" color="gray.400" boxSize={4} />
                            <Input
                                placeholder="Search category..."
                                pl="40px"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </Box>

                        {/* Main Content Area */}
                        <Grid templateColumns="repeat(12, 1fr)" gap={6}>
                            {/* Left Sidebar - Category List */}
                            <GridItem colSpan={3}>
                                <Box borderWidth="1px" borderRadius="lg" h="600px" display="flex" flexDirection="column">
                                    <Box p={4}>
                                        <Heading size="md" fontWeight={"500"}>Categories</Heading>
                                        <Text fontSize="sm" color="gray.600">
                                            {canViewCategories
                                                ? `${categories.length} compliance categories`
                                                : "You don't have permission to view categories."}
                                        </Text>
                                    </Box>
                                    {canViewCategories && (

                                        <Box p={0}>
                                            <Box maxH="600px" overflowY="auto">
                                                {loading ? (
                                                    <Flex justify="center" align="center" minH="200px">
                                                        <Spinner size="lg" color="blue.500" />
                                                    </Flex>
                                                ) : (
                                                    <VStack spacing={1} p={2} align="stretch">
                                                        {filteredCategories.map((category) => (
                                                            <Box
                                                                key={category.id}
                                                                p={4}
                                                                borderRadius="lg"
                                                                borderWidth="1px"
                                                                cursor="pointer"
                                                                bg={selectedCategory?.id === category.id ? 'var(--brand-primary)' : 'white'}
                                                                color={selectedCategory?.id === category.id ? 'white' : 'black'}
                                                                borderColor={selectedCategory?.id === category.id ? 'var(--brand-primary)' : 'transparent'}
                                                                _hover={{ bg: selectedCategory?.id === category.id ? 'var(--brand-primary)' : 'gray.50' }}
                                                                transition="all 0.2s"
                                                            >
                                                                <VStack spacing={3} align="stretch">
                                                                    <Flex justify="space-between" align="center">
                                                                        <HStack spacing={3} flex={1} onClick={() => handleCategorySelect(category)}>
                                                                            <Box
                                                                                p={2}
                                                                                borderRadius="lg"
                                                                                bg={selectedCategory?.id === category.id ? 'whiteAlpha.200' : 'blue.50'}

                                                                            >
                                                                                <Icon as={getIconComponent(category.icon)} boxSize={5} />
                                                                            </Box>
                                                                            <Box flex={1}>
                                                                                <Flex align="center" gap={2} justifyContent={"space-between"}>
                                                                                    <Text fontWeight="medium" fontSize="sm">{category.name}</Text>

                                                                                    {(editPermission && category.customer_id !== 0) && (

                                                                                        <Icon
                                                                                            as={LuFileEdit}
                                                                                            boxSize={4}
                                                                                            color={selectedCategory?.id === category.id ? 'white' : 'gray.500'}
                                                                                            cursor="pointer"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                openEditCategoryModal(category);
                                                                                            }}
                                                                                            // pointerEvents={editPermission === false ? "none" : "pointer"}
                                                                                            _hover={{ color: selectedCategory?.id === category.id ? 'whiteAlpha.700' : 'gray.700' }}
                                                                                        />
                                                                                    )}
                                                                                </Flex>
                                                                                <Text
                                                                                    fontSize="xs"
                                                                                    mt={1}
                                                                                    color={selectedCategory?.id === category.id ? 'whiteAlpha.800' : 'gray.600'}
                                                                                >
                                                                                    {category.keywordCount} keywords
                                                                                </Text>
                                                                            </Box>
                                                                        </HStack>
                                                                    </Flex>

                                                                    <Flex justify="space-between" align="center">

                                                                        <Tag bg={selectedCategory?.id === category.id ? "white" : "var(--brand-tint)"} color={selectedCategory?.id === category.id ? "var(--brand-primary)" : "var(--brand-primary-hover)"} size={"sm"} border={"1px solid"} borderColor={selectedCategory?.id === category.id ? "white" : "#8ec5ff"}>{category?.keyword_matches || 0} Matches</Tag>
                                                                        {category.customer_id !== 0 && (

                                                                            <Switch
                                                                                isChecked={category.enabled}
                                                                                onChange={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleToggleCategoryStatus(category);
                                                                                }}
                                                                                sx={{
                                                                                    'span.chakra-switch__track[data-checked]': {
                                                                                        backgroundColor: 'var(--brand-primary)',
                                                                                    }
                                                                                }}
                                                                                disabled={statusPermission === false ? true : false}
                                                                                size="sm"
                                                                            />
                                                                        )}

                                                                    </Flex>
                                                                </VStack>
                                                            </Box>
                                                        ))}
                                                    </VStack>
                                                )}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </GridItem>

                            {/* Right Panel - Keywords */}
                            <GridItem colSpan={9}>
                                <VStack spacing={6} align="stretch">
                                    {selectedCategory && (
                                        <Box borderWidth="1px" borderRadius="lg" >
                                            <Box>
                                                <Flex justify="space-between" align="center">
                                                    <Box p={4}>
                                                        <Heading size="md" fontWeight={"500"}>{selectedCategory?.name}</Heading>

                                                        {/* <Text fontSize="sm" color="gray.600" mt={1}>Category Status : <Tag bg={selectedCategory?.enabled ? "green" : "red"} size={"sm"} color={selectedCategory.enabled?"white":"white"}>{selectedCategory?.enabled ? "Active" : "In Active"}</Tag></Text> */}

                                                        {/* <Text fontSize="sm" color="gray.600" mt={1}>
                                                            Manage keywords for this category
                                                        </Text> */}
                                                    </Box>

                                                </Flex>
                                            </Box>
                                        </Box>
                                    )}

                                    {!keywordlistPermission ? (
                                        <Box borderWidth="1px" borderRadius="lg">
                                            <Box py={16}>
                                                <VStack spacing={2}>
                                                    <Text fontSize="md" fontWeight="semibold" color="gray.700">No Access</Text>
                                                    <Text fontSize="sm" color="gray.500">You don't have permission to view keywords.</Text>
                                                </VStack>
                                            </Box>
                                        </Box>
                                    ) : selectedCategory ? (
                                        <Box borderWidth="1px" borderRadius="lg" p={4}>
                                            <Box>

                                                <Flex justify="space-between" align="center">
                                                    <Box>
                                                        <Heading size="md" fontWeight={"500"}>Keywords ({selectedCategory.keywordCount || 0})</Heading>
                                                        <Text fontSize="sm" color="gray.600" mt={1}>
                                                            Manage keywords for this category
                                                        </Text>
                                                    </Box>
                                                    {keywordcreatePermission && (

                                                        <Button
                                                            bg="var(--brand-primary)"
                                                            color="white"
                                                            _hover={{ bg: 'var(--brand-primary-hover)' }}
                                                            leftIcon={<Icon as={LuPlus} />}
                                                            onClick={() => setIsAddKeywordOpen(true)}
                                                            size={"sm"}
                                                        >
                                                            Add Keyword
                                                        </Button>
                                                    )}
                                                </Flex>
                                            </Box>
                                            <Box>
                                                <VStack spacing={4} align="stretch">
                                                    {/* Keyword Search */}
                                                    <Box position="relative">
                                                        <Icon as={LuSearch} position="absolute" left="12px" top="50%" transform="translateY(-50%)" color="gray.400" boxSize={4} />
                                                        <Input
                                                            placeholder="Search keywords..."
                                                            pl="40px"
                                                            value={keywordSearchTerm}
                                                            onChange={(e) => setKeywordSearchTerm(e.target.value)}
                                                        />
                                                    </Box>

                                                    {/* Keywords Table */}
                                                    {keywordLoading ? (
                                                        <Flex justify="center" align="center" minH="200px">
                                                            <Spinner size="lg" color="blue.500" />
                                                        </Flex>
                                                    ) : (
                                                        <Box borderWidth="1px" borderRadius="lg" overflowX="auto">
                                                            <Table variant="simple">
                                                                <Thead>
                                                                    <Tr>
                                                                        <Th>Keyword</Th>
                                                                        <Th>Notes</Th>
                                                                        <Th>Status</Th>
                                                                        <Th>Last Updated</Th>
                                                                        <Th textAlign="right">Actions</Th>
                                                                    </Tr>
                                                                </Thead>
                                                                <Tbody>
                                                                    {filteredKeywords.length === 0 ? (
                                                                        <Tr>
                                                                            <Td colSpan={5} textAlign="center" color="gray.500" py={8}>
                                                                                No keywords found
                                                                            </Td>
                                                                        </Tr>
                                                                    ) : (
                                                                        filteredKeywords.map((keyword) => (
                                                                            <Tr key={keyword.id}>
                                                                                <Td fontWeight="medium">{keyword.keyword}</Td>
                                                                                <Td fontSize="sm" color="gray.600">{keyword.notes}</Td>
                                                                                <Td>
                                                                                    <Switch
                                                                                        isChecked={keyword.status === 'active'}
                                                                                        onChange={() => handleToggleKeywordStatus(keyword)}
                                                                                        sx={{
                                                                                            'span.chakra-switch__track[data-checked]': {
                                                                                                backgroundColor: 'var(--brand-primary)',
                                                                                            }
                                                                                        }}
                                                                                        disabled={(keywordStatusPermission === false || keyword?.customer_id === 0) ? true : false}
                                                                                    />
                                                                                </Td>
                                                                                <Td fontSize="sm" color="gray.600">
                                                                                    {keyword.lastUpdated}
                                                                                </Td>
                                                                                <Td textAlign="right">
                                                                                    <HStack spacing={2} justify="flex-end">
                                                                                      
                                                                                        {(keywordeditPermission && keyword?.customer_id !== 0) && (

                                                                                            <IconButton
                                                                                                aria-label="Edit"
                                                                                                icon={<Icon as={LuFileEdit} />}
                                                                                                size="sm"
                                                                                                variant="ghost"
                                                                                                onClick={() => openEditKeywordModal(keyword)}
                                                                                            />
                                                                                        )}
                                                                                        {(keyworddeletePermission && keyword?.customer_id !== 0) && (

                                                                                            <IconButton
                                                                                                aria-label="Delete"
                                                                                                icon={<Icon as={LuTrash2} />}
                                                                                                size="sm"
                                                                                                variant="ghost"
                                                                                                color="red.500"
                                                                                                onClick={() => openDeleteKeywordModal(keyword)}
                                                                                            />
                                                                                        )}
                                                                                    </HStack>
                                                                                </Td>
                                                                            </Tr>
                                                                        ))
                                                                    )}
                                                                </Tbody>
                                                            </Table>
                                                        </Box>
                                                    )}
                                                </VStack>
                                            </Box>
                                        </Box>
                                    ) : !canViewCategories ? (
                                        // RP_007: keywords are always scoped to a category. If the backend
                                        // refuses the category list to a keyword-list-only user, they can't
                                        // select one — explain why instead of a silent empty panel.
                                        <Box borderWidth="1px" borderRadius="lg">
                                            <Box py={16}>
                                                <VStack spacing={4}>
                                                    <Icon as={LuShield} boxSize={16} color="gray.400" opacity={0.5} />
                                                    <Heading size="md" fontWeight={"500"}>Category access required</Heading>
                                                    <Text fontSize="sm" color="gray.600" textAlign="center" maxW="360px">
                                                        Keywords are organized by category. Ask your admin to also grant
                                                        category list access so you can pick a category and manage its keywords.
                                                    </Text>
                                                </VStack>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box borderWidth="1px" borderRadius="lg">
                                            <Box py={16}>
                                                <VStack spacing={4}>
                                                    <Icon as={LuShield} boxSize={16} color="gray.400" opacity={0.5} />
                                                    <Heading size="md" fontWeight={"500"}>Select a Category</Heading>
                                                    <Text fontSize="sm" color="gray.600" textAlign="center">
                                                        Choose a category from the left sidebar to view and manage keywords
                                                    </Text>
                                                </VStack>
                                            </Box>
                                        </Box>
                                    )}
                                </VStack>
                            </GridItem>
                        </Grid>
                    </VStack>

                    {/* Add Category Modal */}
                    <Modal isOpen={isAddCategoryOpen} onClose={() => setIsAddCategoryOpen(false)} size="lg">
                        <ModalOverlay />
                        <ModalContent>
                            <ModalHeader>Add Category</ModalHeader>
                            <ModalCloseButton />
                            <ModalBody>
                                <VStack spacing={4} align="stretch">
                                    <Box>
                                        <Text fontSize="sm" fontWeight="medium" mb={1}>Category Name *</Text>
                                        <Input
                                            placeholder="Enter category name..."
                                            value={newCategoryName}
                                            onChange={(e) => setNewCategoryName(e.target.value)}
                                        />
                                    </Box>
                                </VStack>
                            </ModalBody>
                            <ModalFooter>
                                <HStack spacing={2}>
                                    <Button variant="outline" onClick={() => setIsAddCategoryOpen(false)} size={"sm"}>
                                        Cancel
                                    </Button>
                                    <Button
                                        bg="var(--brand-primary)"
                                        color="white"
                                        _hover={{ bg: 'var(--brand-primary-hover)' }}
                                        leftIcon={<Icon as={LuCheck} />}
                                        onClick={handleCreateCategory}
                                        size={"sm"}
                                        isLoading={createCategoryLoading}
                                        loadingText="Saving..."
                                    >
                                        Save Category
                                    </Button>
                                </HStack>
                            </ModalFooter>
                        </ModalContent>
                    </Modal>

                    {/* Edit Category Modal */}
                    <Modal isOpen={isEditCategoryOpen} onClose={() => setIsEditCategoryOpen(false)} size="lg">
                        <ModalOverlay />
                        <ModalContent>
                            <ModalHeader>Edit Category</ModalHeader>
                            <ModalCloseButton />
                            <ModalBody>
                                <VStack spacing={4} align="stretch">
                                    <Box>
                                        <Text fontSize="sm" fontWeight="medium" mb={1}>Category Name *</Text>
                                        <Input
                                            placeholder="Enter category name..."
                                            value={editCategoryData?.name || ''}
                                            onChange={(e) => setEditCategoryData({ ...editCategoryData, name: e.target.value })}
                                        />
                                    </Box>
                                </VStack>
                            </ModalBody>
                            <ModalFooter>
                                <HStack spacing={2}>
                                    <Button variant="outline" onClick={() => setIsEditCategoryOpen(false)} size={"sm"}>
                                        Cancel
                                    </Button>
                                    <Button
                                        bg="var(--brand-primary)"
                                        color="white"
                                        _hover={{ bg: 'var(--brand-primary-hover)' }}
                                        leftIcon={<Icon as={LuCheck} />}
                                        onClick={handleUpdateCategory}
                                        size={"sm"}
                                        isLoading={updateCategoryLoading}
                                        loadingText="Updating..."
                                    >
                                        Update Category
                                    </Button>
                                </HStack>
                            </ModalFooter>
                        </ModalContent>
                    </Modal>

                    {/* Add Keyword Modal */}
                    <Modal isOpen={isAddKeywordOpen} onClose={() => setIsAddKeywordOpen(false)} size="lg">
                        <ModalOverlay />
                        <ModalContent>
                            <ModalHeader>Add Keyword</ModalHeader>
                            <ModalCloseButton />
                            <ModalBody>
                                <VStack spacing={4} align="stretch">
                                    <Box>
                                        <Text fontSize="sm" fontWeight="medium" mb={1}>Keyword *</Text>
                                        <Input
                                            placeholder="Enter keyword..."
                                            value={newKeyword}
                                            onChange={(e) => setNewKeyword(e.target.value)}
                                        />
                                    </Box>
                                    <Box>
                                        <Text fontSize="sm" fontWeight="medium" mb={1}>Category</Text>
                                        <Input value={selectedCategory?.name || ''} isDisabled />
                                    </Box>
                                    <Box>
                                        <Text fontSize="sm" fontWeight="medium" mb={1}>Notes (Optional)</Text>
                                        <Textarea
                                            placeholder="Add notes about this keyword..."
                                            value={newKeywordNotes}
                                            onChange={(e) => setNewKeywordNotes(e.target.value)}
                                            rows={3}
                                        />
                                    </Box>
                                </VStack>
                            </ModalBody>
                            <ModalFooter>
                                <HStack spacing={2}>
                                    <Button variant="outline" onClick={() => setIsAddKeywordOpen(false)} size={"sm"}>
                                        Cancel
                                    </Button>
                                    <Button
                                        bg="var(--brand-primary)"
                                        color="white"
                                        _hover={{ bg: 'var(--brand-primary-hover)' }}
                                        leftIcon={<Icon as={LuCheck} />}
                                        onClick={handleCreateKeyword}
                                        size={"sm"}
                                        isLoading={createKeywordLoading}
                                        loadingText="Saving..."
                                    >
                                        Save Keyword
                                    </Button>
                                </HStack>
                            </ModalFooter>
                        </ModalContent>
                    </Modal>

                    {/* Edit Keyword Modal */}
                    <Modal isOpen={isEditKeywordOpen} onClose={() => setIsEditKeywordOpen(false)} size="lg">
                        <ModalOverlay />
                        <ModalContent>
                            <ModalHeader>Edit Keyword</ModalHeader>
                            <ModalCloseButton />
                            <ModalBody>
                                <VStack spacing={4} align="stretch">
                                    <Box>
                                        <Text fontSize="sm" fontWeight="medium" mb={1}>Keyword *</Text>
                                        <Input
                                            placeholder="Enter keyword..."
                                            value={editKeywordData?.keyword || ''}
                                            onChange={(e) => setEditKeywordData({ ...editKeywordData, keyword: e.target.value })}
                                        />
                                    </Box>
                                    <Box>
                                        <Text fontSize="sm" fontWeight="medium" mb={1}>Notes (Optional)</Text>
                                        <Textarea
                                            placeholder="Add notes about this keyword..."
                                            value={editKeywordData?.notes || ''}
                                            onChange={(e) => setEditKeywordData({ ...editKeywordData, notes: e.target.value })}
                                            rows={3}
                                        />
                                    </Box>
                                </VStack>
                            </ModalBody>
                            <ModalFooter>
                                <HStack spacing={2}>
                                    <Button variant="outline" onClick={() => setIsEditKeywordOpen(false)} size={"sm"}>
                                        Cancel
                                    </Button>
                                    <Button
                                        bg="var(--brand-primary)"
                                        color="white"
                                        _hover={{ bg: 'var(--brand-primary-hover)' }}
                                        leftIcon={<Icon as={LuCheck} />}
                                        onClick={handleUpdateKeyword}
                                        size={"sm"}
                                        isLoading={updateKeywordLoading}
                                        loadingText="Updating..."
                                    >
                                        Update Keyword
                                    </Button>
                                </HStack>
                            </ModalFooter>
                        </ModalContent>
                    </Modal>

                    {/* Delete Keyword Confirmation Modal */}
                    <Modal isOpen={isDeleteKeywordOpen} onClose={() => setIsDeleteKeywordOpen(false)} size="md">
                        <ModalOverlay />
                        <ModalContent>
                            <ModalHeader>Delete Keyword</ModalHeader>
                            <ModalCloseButton />
                            <ModalBody>
                                <VStack spacing={4} align="stretch">
                                    <Text>
                                        Are you sure you want to delete the keyword <Text as="span" fontWeight="bold">"{keywordToDelete?.keyword}"</Text>? This action cannot be undone.
                                    </Text>
                                </VStack>
                            </ModalBody>
                            <ModalFooter>
                                <HStack spacing={2}>
                                    <Button variant="outline" onClick={() => setIsDeleteKeywordOpen(false)} size={"sm"}>
                                        Cancel
                                    </Button>
                                    <Button
                                        colorScheme="red"
                                        leftIcon={<Icon as={LuTrash2} />}
                                        onClick={handleDeleteKeyword}
                                        size={"sm"}
                                        isLoading={deleteKeywordLoading}
                                        loadingText="Deleting..."
                                    >
                                        Delete
                                    </Button>
                                </HStack>
                            </ModalFooter>
                        </ModalContent>
                    </Modal>
                </Box>

            </BoxWrapper>
        </>
    );
}